import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import svgPanZoom from 'svg-pan-zoom';
import {
  ZoomIn,
  ZoomOut,
  Maximize,
  RefreshCw,
  Download,
  Copy,
  Check,
  X,
  AlertCircle,
} from 'lucide-react';
import { renderMermaid, detectDiagramType } from './renderMermaid';

interface Props {
  code: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MermaidViewerDialog({ code, open, onOpenChange }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pzRef = useRef<SvgPanZoom.Instance | null>(null);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const diagramType = detectDiagramType(code);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);

    const id = `mermaid-viewer-${Math.random().toString(36).slice(2, 9)}`;

    (async () => {
      try {
        const { svg } = await renderMermaid(code, id);
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = svg;
        const svgEl = containerRef.current.querySelector('svg') as SVGSVGElement | null;
        if (!svgEl) return;

        // Make svg fill the container so svg-pan-zoom can compute fit correctly.
        svgEl.setAttribute('width', '100%');
        svgEl.setAttribute('height', '100%');
        svgEl.style.maxWidth = 'none';
        svgEl.style.cursor = 'grab';

        pzRef.current = svgPanZoom(svgEl, {
          controlIconsEnabled: false,
          fit: true,
          center: true,
          minZoom: 0.2,
          maxZoom: 20,
          zoomScaleSensitivity: 0.3,
          dblClickZoomEnabled: true,
          onZoom: (z: number) => setZoom(z),
        });
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to render diagram');
      }
    })();

    return () => {
      cancelled = true;
      try {
        pzRef.current?.destroy();
      } catch {
        /* noop */
      }
      pzRef.current = null;
    };
  }, [open, code]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
        return;
      }
      if (!pzRef.current) return;
      if (e.key === '+' || e.key === '=') {
        pzRef.current.zoomIn();
      } else if (e.key === '-') {
        pzRef.current.zoomOut();
      } else if (e.key === '0') {
        pzRef.current.resetZoom();
        pzRef.current.resetPan();
      } else if (e.key === 'f' || e.key === 'F') {
        pzRef.current.fit();
        pzRef.current.center();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const handleDownload = () => {
    const svgEl = containerRef.current?.querySelector('svg');
    if (!svgEl) return;
    const clone = svgEl.cloneNode(true) as SVGSVGElement;
    // Reset transform applied by svg-pan-zoom so the download is clean.
    const viewport = clone.querySelector('.svg-pan-zoom_viewport');
    if (viewport) viewport.removeAttribute('transform');
    const serialized = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`], {
      type: 'image/svg+xml',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagramType}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!open) return null;

  const toolbarButton = (icon: React.ReactNode, label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      {icon}
    </button>
  );

  // Portal to document.body: DocViewer.tsx applies a CSS `transform` (for its
  // zoom control) to the #doc-content ancestor this dialog would otherwise
  // render inside, and any transformed ancestor becomes the containing block
  // for `position: fixed` descendants — which misplaces a fixed overlay
  // relative to that (scrolled, possibly offset) box instead of the viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm print:hidden"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="relative flex h-[92vh] w-[96vw] max-w-[96vw] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border bg-card/80 backdrop-blur px-4 py-2 pr-12">
          <div className="text-sm text-muted-foreground capitalize">{diagramType}</div>
          <div className="flex items-center gap-1">
            {toolbarButton(<ZoomOut className="h-4 w-4" />, 'Zoom out (-)', () => pzRef.current?.zoomOut())}
            {toolbarButton(<ZoomIn className="h-4 w-4" />, 'Zoom in (+)', () => pzRef.current?.zoomIn())}
            {toolbarButton(<Maximize className="h-4 w-4" />, 'Fit to screen (F)', () => {
              pzRef.current?.fit();
              pzRef.current?.center();
            })}
            {toolbarButton(<RefreshCw className="h-4 w-4" />, 'Reset (0)', () => {
              pzRef.current?.resetZoom();
              pzRef.current?.resetPan();
            })}
            <div className="mx-1 h-5 w-px bg-border" />
            {toolbarButton(copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />, 'Copy source', handleCopy)}
            {toolbarButton(<Download className="h-4 w-4" />, 'Download SVG', handleDownload)}
          </div>
        </div>

        <button
          type="button"
          onClick={() => onOpenChange(false)}
          title="Close"
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative flex-1 bg-background">
          {error ? (
            <div className="p-6">
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                <AlertCircle className="mb-1 h-4 w-4" />
                <strong>Error rendering diagram:</strong> {error}
              </div>
            </div>
          ) : (
            <>
              <div ref={containerRef} className="absolute inset-0" />
              <div className="absolute bottom-3 right-3 select-none rounded-full bg-card/80 px-3 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur border border-border">
                {Math.round(zoom * 100)}%
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default MermaidViewerDialog;
